import type { Editor } from '@tiptap/core';
import { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';

interface LinkPopoverProps {
  editor: Editor;
}

export const LinkPopover = ({ editor }: LinkPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isLinkActive, setIsLinkActive] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Dynamic link detection
  useEffect(() => {
    const updateLinkState = () => {
      const linkActive = editor.isActive('link');
      setIsLinkActive(linkActive);
    };

    // Update state immediately
    updateLinkState();

    // Listen to selection changes
    editor.on('selectionUpdate', updateLinkState);
    editor.on('focus', updateLinkState);
    editor.on('blur', updateLinkState);

    return () => {
      editor.off('selectionUpdate', updateLinkState);
      editor.off('focus', updateLinkState);
      editor.off('blur', updateLinkState);
    };
  }, [editor]);

  // URL validation
  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  // Handle URL change
  const handleUrlChange = (value: string) => {
    setUrl(value);
    setIsValidUrl(validateUrl(value));
  };

  // Set link
  const setLink = () => {
    if (!editor.isEditable) return;
    if (url && isValidUrl) {
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: fullUrl }).run();
    } else if (!url) {
      editor.chain().focus().unsetLink().run();
    }
    setIsOpen(false);
    setUrl('');
  };

  // Remove link
  const removeLink = () => {
    if (!editor.isEditable) return;
    editor.chain().focus().unsetLink().run();
    setIsOpen(false);
    setUrl('');
  };

  // Open popover
  const openPopover = () => {
    if (!editor.isEditable) return;
    setIsOpen(true);
    const currentUrl = editor.getAttributes('link').href || '';
    setUrl(currentUrl);
    setIsValidUrl(true);
  };

  // Handle keydown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setLink();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setUrl('');
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setUrl('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus input when popover opens
      setTimeout(() => inputRef.current?.focus(), 0);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-open when link is selected
  useEffect(() => {
    if (isLinkActive && !isOpen) {
      const currentUrl = editor.getAttributes('link').href || '';
      setUrl(currentUrl);
      setIsValidUrl(true);
    }
  }, [isLinkActive, isOpen, editor]);

  return (
    <div className="link-popover-container" ref={popoverRef}>
      <Button
        onClick={openPopover}
        type={isLinkActive ? 'primary' : 'default'}
        title="Add/Edit Link"
        disabled={!editor.isEditable}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>

      {isOpen && (
        <div className="link-popover">
          <div className="link-popover-header">
            <span className="link-popover-title">Link</span>
            {isLinkActive && (
              <Button onClick={removeLink} type="text" size="small" title="Remove Link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 6h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            )}
          </div>

          <div className="link-popover-content">
            <div className="link-input-group">
              <input
                ref={inputRef}
                type="text"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter URL"
                className={`link-input ${!isValidUrl && url ? 'invalid' : ''}`}
              />
              {!isValidUrl && url && <div className="link-error">Please enter a valid URL</div>}
            </div>

            <div className="link-popover-actions">
              <Button onClick={setLink} type="primary" disabled={!isValidUrl}>
                Save
              </Button>
              <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
